'use client';

import React, { useEffect, useState } from 'react';
import { ShieldCheck, LogIn, Loader2, AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';



interface GSCSite {
  siteUrl: string;
  permissionLevel: string;
}

interface GSCConnectBannerProps {
  onConnected: (siteUrl: string) => void;
  selectedSiteUrl?: string;
}

export default function GSCConnectBanner({ onConnected, selectedSiteUrl }: GSCConnectBannerProps) {
  const [status, setStatus] = useState<'checking' | 'disconnected' | 'connected'>('checking');
  const [sites, setSites] = useState<GSCSite[]>([]);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gsc_connected') === '1') {
      toast.success('Google Search Console connected!', {
        description: 'Your GSC account is now linked. Select a property to load data.',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('gsc_error')) {
      const errMsg = params.get('gsc_error');
      toast.error('GSC connection failed', {
        description:
          errMsg === 'access_denied' ?'Access was denied. Please try again.'
            : `Error: ${errMsg}`,
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const res = await fetch('/api/gsc/sites');
      console.log(res);
      const data = await res.json();
      if (data.authenticated && data.sites?.length > 0) {
        setStatus('connected');
        setSites(data.sites);
        if (!selectedSiteUrl) {
          onConnected(data.sites[0].siteUrl);
        }
      } else {
        setStatus('disconnected');
      }
    } catch {
      setStatus('disconnected');
    }
  };

  const handleConnect = () => {
    window.location.href = '/api/gsc/auth';
  };

  if (status === 'checking') {
    return (
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-[5px] text-sm text-[#696969]"
        style={{
          backgroundColor: 'rgba(150, 25, 50, 0.04)',
          border: '1px solid rgba(150, 25, 50, 0.15)',
        }}
      >
        <Loader2 size={14} className="animate-spin text-[#961932]" />
        <span>Checking GSC connection…</span>
      </div>
    );
  }

  if (status === 'disconnected') {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-[5px]"
        style={{
          backgroundColor: 'rgba(150, 25, 50, 0.06)',
          border: '1px solid rgba(150, 25, 50, 0.2)',
        }}
      >
        <AlertCircle size={16} className="text-[#961932] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#39364E]">
            Google Search Console not connected
          </p>
          <p className="text-xs text-[#961932] mt-0.5">
            Connect your GSC account to load real branded query data
          </p>
        </div>
        <button
          onClick={handleConnect}
          className="flex items-center gap-2 px-4 py-2 bg-[#961932] text-white rounded-[5px] text-sm font-medium hover:bg-[#8C0B31] transition-colors duration-150 flex-shrink-0 btn-smarty-shadow"
        >
          <LogIn size={14} />
          <span>Connect GSC</span>
        </button>
      </div>
    );
  }

  // Connected state
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-[5px]"
      style={{
        backgroundColor: 'rgba(150, 25, 50, 0.04)',
        border: '1px solid rgba(150, 25, 50, 0.15)',
      }}
    >
      <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#39364E]">Google Search Console connected</p>
        <p className="text-xs text-[#696969] mt-0.5">
          {sites.length} propert{sites.length === 1 ? 'y' : 'ies'} available
        </p>
      </div>
      {sites.length > 1 && (
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[rgba(150,25,50,0.2)] rounded-[5px] text-xs font-medium text-[#39364E] hover:border-[#961932] hover:bg-[rgba(150,25,50,0.02)] transition-all duration-150"
          >
            <ShieldCheck size={12} className="text-[#961932]" />
            <span className="max-w-[160px] truncate">{selectedSiteUrl ?? sites[0]?.siteUrl}</span>
            <ChevronDown
              size={12}
              className={`transition-transform text-[#696969] ${
                siteDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {siteDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.1)_0px_8px_24px] z-30 py-1">
              {sites.map((site) => (
                <button
                  key={site.siteUrl}
                  onClick={() => {
                    onConnected(site.siteUrl);
                    setSiteDropdownOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-[rgba(150,25,50,0.04)] transition-colors truncate ${
                    site.siteUrl === selectedSiteUrl
                      ? 'text-[#961932] font-medium'
                      : 'text-[#39364E]'
                  }`}
                >
                  {site.siteUrl}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
